import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { 
  TextField, 
  Chip
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { tagMappingService } from '../../services/tagMappingService';
import { useExcludedChipsStore } from '../../store/excludedChips.store';
import '../../assets/scss/_chip-styling.scss';
import './CategorizedAutocomplete.css';

const CategorizedAutocomplete = ({ 
  options = [],
  value = [],
  onChange,
  placeholder = "Select tags",
  className = "",
  section = 'tags', // Add section prop for chip exclusion
  getChipKey,      // Add getChipKey prop
  handleChipToggle, // Add handleChipToggle prop  
  handleChipRemove, // Add handleChipRemove prop
  showActionButtons = true, // Add prop to control + and - buttons visibility
  renderInput: originalRenderInput, // Extract the original renderInput
  onInputChange: originalOnInputChange, // Extract original onInputChange
  onKeyDown: originalOnKeyDown, // Extract original onKeyDown
  ...otherProps 
}) => {
  const [viewMode, setViewMode] = useState('categories'); // 'categories', 'category-tags', 'search'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [categorizedTags, setCategorizedTags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredOption, setHoveredOption] = useState(null);
  
  // Get excluded chips store
  const { excludedChips } = useExcludedChipsStore();

  // Handle adding chip directly in exclude mode from dropdown
  const handleDirectExclude = (option) => {
    // Check if chip is already selected
    const isAlreadySelected = value.some(item => item.id === option.id);
    
    // First, add to selected items if not already there
    let newValue = value;
    if (!isAlreadySelected) {
      newValue = [...value, option];
      // Update the selection state
      if (onChange) {
        onChange(null, newValue, 'select-option');
      }
    }
    
    // Then mark as excluded using a small delay to ensure state is updated
    setTimeout(() => {
      if (handleChipToggle) {
        handleChipToggle(option, section);
      }
    }, 0);
  };

  // Load and categorize tags when options change
  useEffect(() => {
    const loadCategories = async () => {
      if (options.length === 0) {
        setCategorizedTags({});
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        await tagMappingService.loadMappingsToCache(false);
        
        // Extract tag names from options (assuming options have name or label property)
        const tagNames = options.map(option => option.name || option.label || option);
        
        // Use the service to categorize tags
        const categorized = tagMappingService.categorizeTags(tagNames);
        
        // Convert back to option objects
        const categorizedOptions = {};
        Object.keys(categorized).forEach(category => {
          categorizedOptions[category] = categorized[category]
            .map(tagName => options.find(opt => (opt.name || opt.label || opt) === tagName))
            .filter(Boolean); // Remove any undefined entries
        });
        
        setCategorizedTags(categorizedOptions);
      } catch (error) {
        console.error('Error loading tag categories:', error);
        // Fallback: put all tags in 'other' category
        setCategorizedTags({ other: options });
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [options]);

  // Category metadata (simplified - no emojis or descriptions)
  const categoryConfig = useMemo(() => ({
    moods: { label: 'Moods & Feelings' },
    instruments: { label: 'Instruments' },
    energy: { label: 'Energy & Tempo' },
    period: { label: 'Period & Era' },
    style: { label: 'Style & Genre' },
    location: { label: 'Location & Culture' },
    other: { label: 'Other' }
  }), []);

  // Get options to display based on current view mode
  const displayOptions = useMemo(() => {
    if (isLoading) return [];
    
    if (viewMode === 'search') {
      // Regular search across all options
      return options.filter(option => {
        const optionText = (option.name || option.label || option).toLowerCase();
        return optionText.includes(searchText.toLowerCase());
      });
    }
    
    if (viewMode === 'categories') {
      // Return category objects for display
      return Object.keys(categorizedTags)
        .filter(category => categorizedTags[category].length > 0)
        .map(categoryKey => ({
          type: 'category',
          categoryKey,
          ...categoryConfig[categoryKey]
        }));
    }
    
    if (viewMode === 'category-tags' && selectedCategory) {
      // Return tags in selected category, with back button
      const backButton = {
        type: 'back-button',
        id: '__back__',
        name: '← Back to Categories',
        isBackButton: true
      };
      
      return [backButton, ...(categorizedTags[selectedCategory] || [])];
    }
    
    return [];
  }, [viewMode, selectedCategory, searchText, options, categorizedTags, isLoading, categoryConfig]);


  // Handle option selection
  const handleChange = (event, newValue, reason) => {
    if (reason === 'select-option') {
      const selectedOption = newValue[newValue.length - 1]; // Get the newly selected option
      
      if (selectedOption?.type === 'category') {
        // Category selected - switch to category tags view
        setSelectedCategory(selectedOption.categoryKey);
        setViewMode('category-tags');
        return; // Don't call parent onChange
      }
      
      if (selectedOption?.isBackButton) {
        // Back button clicked - return to categories
        setViewMode('categories');
        setSelectedCategory(null);
        return; // Don't call parent onChange
      }
      
      // Regular tag selected - call parent onChange but exclude our special items
      const filteredValue = newValue.filter(item => 
        item.type !== 'category' && !item.isBackButton
      );
      
      // Clear search text when a tag is selected
      setSearchText('');
      // Stay in the current category view - don't reset to categories
      
      if (onChange) {
        onChange(event, filteredValue, reason);
      }
    } else {
      // Handle other change reasons (clear, remove-option, etc.)
      const filteredValue = newValue?.filter(item => 
        item?.type !== 'category' && !item?.isBackButton
      ) || [];
      
      if (onChange) {
        onChange(event, filteredValue, reason);
      }
    }
  };

  // Handle dropdown open/close
  const handleOpen = () => {
    setIsOpen(true);
    setViewMode('categories');
    setSearchText('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setViewMode('categories');
    setSelectedCategory(null);
    setSearchText('');
  };

  // Custom option rendering with hover buttons like other autocompletes
  const renderOption = (option, { selected, focused }) => {
    if (option.type === 'category') {
      return (
        <div 
          style={{ 
            backgroundColor: selected
              ? 'var(--accent-1-color)'
              : focused
                ? 'rgba(255, 0, 0, 0.5)'
                : 'transparent',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: selected ? '8px' : undefined,
            padding: selected ? '4px 16px' : '4px 16px',
            margin: selected ? '2px 0' : '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: '80%',
            transition: 'background 0.15s, color 0.15s',
            position: 'relative'
          }}
        >
          <span className="category-option-content">- {option.label} -</span>
          {/* Invisible spacer to match the height of tag items with buttons */}
          <div style={{ height: '16px', marginLeft: '8px', minWidth: '40px' }}></div>
        </div>
      );
    }
    
    if (option.isBackButton) {
      return (
        <div 
          style={{ 
            backgroundColor: selected
              ? 'var(--accent-1-color)'
              : focused
                ? 'rgba(255, 0, 0, 0.5)'
                : 'transparent',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: selected ? '8px' : undefined,
            padding: selected ? '4px 16px' : '4px 16px',
            margin: selected ? '2px 0' : '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: '80%',
            transition: 'background 0.15s, color 0.15s',
            position: 'relative'
          }}
        >
          <div className="back-button-option-content" style={{ display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon style={{ marginRight: 8, fontSize: 18 }} />
            Back
          </div>
          {/* Invisible spacer to match the height of tag items with buttons */}
          <div style={{ height: '16px', marginLeft: '8px', minWidth: '40px' }}></div>
        </div>
      );
    }
    
    // Regular tag option with + and - buttons on hover
    const optionKey = `${section}_${option.id || option.name}`;
    const isHovered = hoveredOption === optionKey;
    
    return (
      <div 
        style={{ 
          backgroundColor: selected
            ? 'var(--accent-1-color)'
            : focused
              ? 'rgba(255, 0, 0, 0.5)'
              : 'transparent',
          color: '#fff',
          cursor: 'pointer',
          borderRadius: selected ? '8px' : undefined,
          padding: selected ? '4px 16px' : '4px 16px',
          margin: selected ? '2px 0' : '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: '80%',
          transition: 'background 0.15s, color 0.15s',
          position: 'relative'
        }}
        onMouseEnter={() => setHoveredOption(optionKey)}
        onMouseLeave={() => setHoveredOption(null)}
      >
        <span>{option.name || option.label || option}</span>
        {isHovered && showActionButtons && (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', height: '16px' }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Add the item as included (same as clicking on the item text)
                if (!value.some(item => item.id === option.id)) {
                  if (onChange) {
                    onChange(e, [...value, option], 'select-option');
                  }
                }
              }}
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                fontSize: '18px', 
                fontWeight: 'bold',
                marginRight: '4px',
                lineHeight: '1',
                transition: 'color 0.15s ease',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '0 4px',
                minWidth: '16px',
                height: '16px'
              }}
              onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.5)'}
              title="Add this tag to filter"
            >
              +
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDirectExclude(option);
              }}
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '18px',
                fontWeight: 'bold',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '0 4px',
                cursor: 'pointer',
                minWidth: '16px',
                height: '16px',
                lineHeight: '1',
                transition: 'color 0.15s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.5)'}
              title="Add this tag and exclude it from results"
            >
              -
            </button>
          </div>
        )}
        {!showActionButtons && (
          <div style={{ height: '16px', marginLeft: '8px', minWidth: '40px' }}></div>
        )}
      </div>
    );
  };

  // Get option label
  const getOptionLabel = (option) => {
    if (option.type === 'category') return '';
    if (option.isBackButton) return '';
    return option.name || option.label || option;
  };

  // Filter selected options to exclude our special items
  const filteredValue = value?.filter(item => 
    item?.type !== 'category' && !item?.isBackButton
  ) || [];

  return (
    <Autocomplete
      {...otherProps}
      multiple
      options={displayOptions}
      value={filteredValue}
      onChange={handleChange}
      onKeyDown={originalOnKeyDown} // Use the original onKeyDown from parent
      onOpen={handleOpen}
      onClose={handleClose}
      open={isOpen}
      inputValue={searchText} // Control the input value
      onInputChange={(event, newInputValue, reason) => {
        // Ignore reset events caused by option changes - only accept input events
        if (reason === 'reset') {
          return;
        }
        
        setSearchText(newInputValue);
        
        if (newInputValue.trim().length > 0) {
          setViewMode('search');
        } else {
          setViewMode('categories');
        }
      }}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={(params) => {
        if (originalRenderInput) {
          // Use the original renderInput but update the placeholder if needed
          const originalResult = originalRenderInput(params);
          return React.cloneElement(originalResult, {
            ...originalResult.props,
            placeholder: isLoading ? "Loading categories..." : (originalResult.props.placeholder || placeholder)
          });
        }
        
        // Fallback if no original renderInput
        return (
          <TextField
            {...params}
            variant="outlined"
            placeholder={isLoading ? "Loading categories..." : placeholder}
            className={className}
          />
        );
      }}
      ListboxProps={{
        style: {
          fontSize: 'var(--menu-font-size)'
        }
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const chipKey = getChipKey ? getChipKey(option, section) : `${section}_${option.id || option.name}`;
          const isExcluded = excludedChips[chipKey];
          const tagProps = getTagProps({ index });
          
          // Only keep the size and variant props, remove event handlers
          const { onDelete, onClick, onKeyDown, onMouseDown, ...safeTagProps } = tagProps;
          
          return (
            <Chip
              key={option.id || option.name || option.label || index}
              label={option.name || option.label || option}
              size={safeTagProps.size}
              variant={safeTagProps.variant}
              className={`custom-chip ${isExcluded ? 'excluded-chip' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (handleChipToggle) {
                  handleChipToggle(option, section);
                }
              }}
              onDelete={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (handleChipRemove) {
                  // The handleChipRemove function expects: (chip, section, selectedItems, setSelectedItems)
                  // We need to provide a setSelectedItems function that calls our onChange
                  const setSelectedItems = (newValueOrUpdater) => {
                    if (typeof newValueOrUpdater === 'function') {
                      // Handle updater function
                      const newValue = newValueOrUpdater(value || []);
                      if (onChange) {
                        onChange(e, newValue);
                      }
                    } else {
                      // Handle direct value
                      if (onChange) {
                        onChange(e, newValueOrUpdater || []);
                      }
                    }
                  };
                  handleChipRemove(option, section, value || [], setSelectedItems);
                } else {
                  // Default removal behavior when no handleChipRemove is provided
                  const newValue = value.filter((item, i) => i !== index);
                  if (onChange) {
                    onChange(e, newValue, 'remove-option');
                  }
                }
              }}
              title={isExcluded ? 'Click to include this item in search' : 'Click to exclude this item from search'}
              style={{ cursor: 'pointer' }}
            />
          );
        })
      }
      disableCloseOnSelect
      noOptionsText={
        viewMode === 'search' 
          ? "No tags found matching your search" 
          : "No options available"
      }
      loading={isLoading}
      className="categorized-autocomplete" // Only our identifier for special styling
    />
  );
};

export default CategorizedAutocomplete;